import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { SUCCESS_REDIRECT_DELAY_MS } from "@/config/navigation";
import {
  forgotPasswordAsync,
  resetPasswordAsync,
  resendCodeAsync,
  resetPasswordResetState,
  clearPasswordResetError,
  decrementResendCooldown,
  selectPasswordReset,
} from "@/store/slices/passwordResetSlice";

/**
 * Password Reset State (from Redux)
 */
export interface PasswordResetViewModelState {
  email: string;
  isRequesting: boolean;
  isResetting: boolean;
  error: string | null;
  resendCooldown: number;
}

/**
 * Password Reset Actions (user intents)
 */
export interface PasswordResetViewModelActions {
  requestReset: (email: string) => Promise<void>;
  resetPassword: (code: string, newPassword: string) => Promise<void>;
  resendCode: () => Promise<void>;
  clearError: () => void;
  navigateToLogin: () => void;
}

/**
 * Password Reset Computed Properties
 */
export interface PasswordResetViewModelComputed {
  canSubmitRequest: boolean;
  canSubmitReset: boolean;
  canResend: boolean;
  hasError: boolean;
  errorMessage: string;
  showResetForm: boolean;
}

/**
 * Password Reset ViewModel Return Type
 */
export interface PasswordResetViewModel {
  state: PasswordResetViewModelState;
  actions: PasswordResetViewModelActions;
  computed: PasswordResetViewModelComputed;
  onMount: () => (() => void) | void;
}

/**
 * usePasswordResetViewModel Hook
 *
 * MVVM ViewModel for password reset flow.
 * Structure: { state, actions, computed, onMount }
 *
 * @returns ViewModel with all password reset logic
 */
export function usePasswordResetViewModel(): PasswordResetViewModel {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const passwordReset = useAppSelector(selectPasswordReset);

  // State (mapped from Redux)
  const state: PasswordResetViewModelState = {
    email: passwordReset.email,
    isRequesting: passwordReset.status === "requesting",
    isResetting: passwordReset.status === "resetting",
    error: passwordReset.error,
    resendCooldown: passwordReset.resendCooldown,
  };

  // Actions (all user intents)
  // Note: React Compiler automatically memoizes these functions
  const actions: PasswordResetViewModelActions = {
    requestReset: async (email: string) => {
      try {
        await dispatch(forgotPasswordAsync(email)).unwrap();
        toast.success(t("passwordReset.success.codeSent"));
        // Navigate to reset password page
        navigate("/auth/reset-password");
      } catch {
        // Error is already in Redux state
      }
    },

    resetPassword: async (code: string, newPassword: string) => {
      if (!state.email) {
        return;
      }

      try {
        await dispatch(
          resetPasswordAsync({
            email: state.email,
            code,
            newPassword,
          })
        ).unwrap();

        // Show success toast with security notice
        toast.success(t("passwordReset.success.passwordReset"), {
          description: t("passwordReset.success.securityNotice"),
          duration: SUCCESS_REDIRECT_DELAY_MS,
        });

        // Delay before redirect
        setTimeout(() => {
          navigate("/auth/login", { replace: true });
        }, SUCCESS_REDIRECT_DELAY_MS);
      } catch {
        // Error is already in Redux state
      }
    },

    resendCode: async () => {
      if (!state.email || state.resendCooldown > 0) {
        return;
      }

      try {
        await dispatch(resendCodeAsync(state.email)).unwrap();
        toast.success(t("passwordReset.success.codeSent"));
      } catch {
        // Error is already in Redux state
      }
    },

    clearError: () => {
      dispatch(clearPasswordResetError());
    },

    navigateToLogin: () => {
      navigate("/auth/login");
    },
  };

  // Computed properties
  const computed: PasswordResetViewModelComputed = {
    canSubmitRequest: !state.isRequesting,
    canSubmitReset: !state.isResetting,
    canResend: state.resendCooldown === 0,
    hasError: state.error !== null,
    errorMessage: state.error ? t(state.error) : "",
    showResetForm: state.email !== "", // Show reset form if email is stored
  };

  // Lifecycle
  // Note: React Compiler automatically memoizes this function
  const onMount = () => {
    // Reset state on mount
    dispatch(resetPasswordResetState());

    // Cleanup function
    return () => {
      dispatch(clearPasswordResetError());
    };
  };

  // Cooldown timer effect (separate from onMount)
  useEffect(() => {
    if (state.resendCooldown > 0) {
      const timer = setInterval(() => {
        dispatch(decrementResendCooldown());
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [state.resendCooldown, dispatch]);

  return { state, actions, computed, onMount };
}
